import { GuaranteesApplicationStatus } from '~/store/guarantees/types.ts';
import type { TGuaranteesApplicationStatusConfig } from './types.ts';

export const GUARANTEES_TENDER_DATE_FORMAT = 'DD.MM.YYYY HH:mm';
export const GUARANTEES_SEARCH_CACHE_PREFIX = 'guarantees:last-selected-edrpou:v1';
export const GUARANTEES_APPLICATIONS_DEFAULT_ROWS_PER_PAGE = 10;
export const GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM = 'contragentId';
export const GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM = 'reviewMode';
export const GUARANTEES_APPLICATION_AM_REVIEW_MODE = 'am';
export const GUARANTEES_APPLICATION_MANUAL_TASK_QUERY_PARAM = 'manualTask';

export const GUARANTEES_TENDER_ID_PATTERN = /^ua-\d{4}-\d{2}-\d{2}-\d{6}-[a-z]$/i;

export const normalizeGuaranteesTenderId = (value: string): string => value.trim().toLowerCase();
export const normalizeGuaranteesTenderIdForBackend = (value: string): string =>
  value.trim().replace(/^ua(?=-)/i, 'UA');

export const guaranteesClientText = {
  pageTitle: 'Видача гарантій',
  applicationPageTitle: 'Заявка на гарантію',
  backButton: 'Повернутися до пошуку',
  backToApplicationsButton: 'Повернутися до заявок',
  tenderSearchTitle: 'Пошук тендера',
  tenderSearchPlaceholder: 'Наприклад, ua-2026-05-29-000001-a',
  tenderSearchButton: 'Знайти тендер',
  tenderSearchButtonLoading: 'Шукаємо тендер...',
  createApplicationButton: 'Створити заявку на гарантію',
  createApplicationUnavailable: 'Створення заявки на гарантію буде доступне пізніше.',
  tenderNotFound: 'За вказаним Tender ID даних поки не знайдено.',
  tenderGuaranteeExpiryDateNotFound:
    'У даних Prozorro не знайдено дату закінчення гарантії. Потрібно уточнити поле у бізнесу.',
  tenderSummaryTitle: 'Інформація про тендер',
  customerSectionTitle: 'Замовник',
  lotsTitle: 'Лоти закупівлі',
  applicationsTitle: 'Заявки на гарантії',
  emptyApplications: 'Заявки відсутні',
  emptyLots: 'Предмети закупівлі відсутні',
  openApplicationButton: 'Відкрити',
  openApplicationUnavailable: 'Перехід до заявки буде доступний пізніше.',
  emptyValue: '-',
  contragentIdLabel: 'ЄДРПОУ',
  applicationIdLabel: 'ID заявки',
  tenderIdLabel: 'Tender ID',
  tenderInternalIdLabel: 'Внутрішній ID',
  tenderDateModifiedLabel: 'Дата зміни tender',
  tenderLastSyncedAtLabel: 'Остання синхронізація',
  tenderTitleLabel: 'Назва тендера',
  tenderDescriptionLabel: 'Опис тендера',
  tenderValueLabel: 'Очікувана вартість',
  tenderProcuringEntityLabel: 'Замовник',
  tenderPeriodStartLabel: 'Початок подання',
  tenderPeriodEndLabel: 'Завершення подання',
  statusLabel: 'Статус',
  createdAtLabel: 'Дата створення',
  updatedAtLabel: 'Дата оновлення',
  expectedValueLabel: 'Очікувана вартість',
  minimalStepLabel: 'Мінімальний крок',
  procurementMethodLabel: 'Метод закупівлі',
  procurementMethodTypeLabel: 'Тип процедури',
  awardCriteriaLabel: 'Критерій вибору',
  ownerLabel: 'Майданчик',
  tenderPeriodLabel: 'Період подачі',
  enquiryPeriodLabel: 'Період уточнень',
  customerLabel: 'Замовник',
  customerCodeLabel: 'ЄДРПОУ',
  customerLegalNameLabel: 'Юридична назва',
  customerAddressLabel: 'Адреса',
  contactNameLabel: 'Контактна особа',
  contactEmailLabel: 'Email',
  contactTelephoneLabel: 'Телефон',
  contactUrlLabel: 'Сайт',
  applicationsNumberLabel: '№ заявки',
  temporaryApplicationNumberTooltip:
    'Для генерації постійного номера заповніть та відправте заявку',
  applicationsCreatedAtLabel: 'Дата створення',
  applicationsGuaranteeAmountLabel: 'Сума гарантії',
  actionsLabel: 'Дії',
  applicationStatusTitle: 'Статус заявки',
  applicationTimelineTitle: 'Історія заявки',
  applicationTimelineEmpty: 'Історія заявки відсутня',
  timelineDecisionLabel: 'Рішення',
  timelinePerformerLabel: 'Виконавець',
  timelineCommentLabel: 'Коментар',
  lotsNameLabel: 'Назва',
  lotsQuantityLabel: 'Кількість',
  lotsUnitLabel: 'Одиниця виміру',
  lotsClassificationLabel: 'Класифікація',
  vatIncludedLabel: 'з ПДВ',
  vatExcludedLabel: 'без ПДВ',
  rootSectionTitle: 'Тип гарантії',
  rootSectionDescription:
    'Оберіть тип гарантійної операції. Від цього залежить набір наступних полів заявки.',
  rootSectionStepLabel: '01',
  guaranteeOperationTypeLabel: 'Тип гарантійної операції',
  guaranteeOperationTypePlaceholder: 'Оберіть тип гарантії',
  guaranteeOperationTypeOtherLabel: 'Уточнення типу гарантії',
  guaranteeOperationTypeOtherPlaceholder: 'Опишіть інший тип гарантії',
  guaranteeAmountLabel: 'Сума гарантії',
  guaranteeCurrencyLabel: 'Валюта',
  guaranteeCurrencyPlaceholder: 'Оберіть валюту',
  guaranteeExpiryDateLabel: 'Дата закінчення гарантії',
  urgentReviewLabel: 'Терміновий розгляд Банком',
  securityTypeLabel: 'Вид забезпечення',
  securityTypePlaceholder: 'Оберіть тип забезпечення',
  securityAmountLabel: 'Сума забезпечення/покриття',
  securityTypeRequiredHint: 'Оберіть тип забезпечення, щоб вибрати рахунок.',
  saveButton: 'Зберегти',
  checkApplicationButton: 'Перевірити заявку та надіслати',
  applicationSubmitSuccessMessage: 'Заявку успішно надіслано',
  applicationSubmitSuccessButton: 'До списку заявок',
  nextButton: 'Далі',
  nextStepTitle: 'Крок 2',
  nextStepDescription: 'Наступний крок заявки на гарантію буде додано пізніше.',
  prozorroSearchTitle: 'Пошук тендеру Prozorro',
  prozorroSearchDescription: 'Блок доступний лише для типу гарантії Prozorro',
  prozorroSearchStepLabel: '02',
  prozorroSearchLabel: 'Tender ID',
  prozorroSearchPlaceholder: 'Наприклад, UA-2026-05-29-000001-A',
  prozorroSearchButton: 'Знайти',
  selectButton: 'Обрати',
  selectOneLotLabel: 'Оберіть один або декілька лотів',
  selectedLotsTotalLabel: 'Загальна сума вибраних лотів',
  selectLotBeforeTenderBindHint: "Оберіть лот, щоб прив'язати тендер до заявки.",
  contractSectionTitle: 'Вибір договору',
  contractSectionDescription:
    'Оберіть договір серед тих, яки Ви раніше уклали з АТ “Укрексімбанком',
  contractSectionStepLabel: '03',
  contractFieldLabel: 'Договір',
  contractFieldPlaceholder: 'Оберіть договір',
  contractLoadingText: 'Завантаження договорів...',
  contractNoOptionsText: 'Договори відсутні',
  contractMissingMainDealId: 'Не отримано id основного договору.',
  saveContractButton: 'Зберегти договір',
  accountSectionTitle: 'Вибір рахунку',
  accountSectionDescription: 'Оберіть рахунок зі списку або введіть IBAN вручну.',
  accountSectionStepLabel: '04',
  accountFieldLabel: 'Рахунок',
  accountFieldPlaceholder: 'Оберіть рахунок',
  accountLoadingText: 'Завантаження рахунків...',
  accountNoOptionsText: 'Рахунки відсутні',
  principalDebitAccountSectionLabel:
    'Поточний IBAN рахунок Принципала для дебетування за Гарантією',
  securityAccountDetailsLabel: 'Реквізити депозитного рахунку або рахунку покриття',
  counterpartyAccountLabel: 'Наявний рахунок контрагента',
  manualAccountIbanLabel: 'IBAN рахунку',
  manualAccountIbanPlaceholder: 'UA...',
  mainDealDateLabel: 'Дата договору',
  mainDealNumberLabel: 'Номер основного договору',
  mainDealDateFromLabel: 'Дата договору з',
  mainDealDateToLabel: 'Дата договору по',
  mainDealCurrencyLabel: 'Валюта договору',
  mainDealAmountLabel: 'Сума договору',
  mainDealLimitBalanceLabel: 'Залишок ліміту',
  accountIbanLabel: 'IBAN',
  accountFrozenLabel: 'Блокування / арешти',
  accountOurBankLabel: 'Наш банк',
  accountTypeLabel: 'Тип',
  principalSectionTitle: 'Інформація про Принципала',
  principalTooltip: 'Принципал - особа, за заявою якої надається гарантія',
  principalSectionDescription: 'Перевірте та оновіть дані принципала для заявки.',
  principalSectionStepLabel: '05',
  principalIdentifyCodeLabel: 'Ідентифікаційний код',
  principalFullNameLabel: 'Повне найменування',
  principalShortNameLabel: 'Скорочене найменування',
  principalLocationLabel: 'Місцезнаходження',
  principalNameAddressGuaranteeLangLabel: 'Найменування та адреса мовою гарантії',
  principalNameAddressGuaranteeLangTooltip: 'Поле є опціональним',
  principalContactFullNameLabel: 'ПІБ контактної особи',
  principalContactEmailLabel: 'Електронна адреса контактної особи',
  principalContactPhoneLabel: 'Номер телефону контактної особи',
  principalAuthorizedPersonPositionLabel: 'Посада уповноваженої особи',
  principalAuthorizedPersonNameLabel: 'ПІБ уповноваженої особи',
  principalLoadingText: 'Завантаження даних принципала...',
  principalSelectAccountHint: 'Оберіть рахунок, щоб відредагувати інформацію про принципала.',
  savePrincipalButton: 'Зберегти інформацію про принципала',
  beneficiarySectionTitle: 'Інформація про Бенефіціара',
  beneficiarySectionDescription: 'Перевірте та оновіть дані бенефіціара для заявки.',
  beneficiarySectionStepLabel: '06',
  beneficiaryNameLabel: 'Найменування',
  beneficiaryIdentifyCodeLabel: 'Ідентифікаційний код',
  beneficiaryLocationLabel: 'Місцезнаходження',
  beneficiaryNameGuaranteeLangLabel: 'Найменування мовою гарантії',
  beneficiaryNonResidentLabel: 'Бенефіціар є нерезидентом України',
  beneficiaryResidenceCountryLabel: 'Країна резидентності Бенефіціара',
  beneficiaryGuarantorBankLabel: 'Банк-гарант бенефіціара',
  beneficiaryBankLabel: 'Банк бенефіціара',
  advisingBankLabel: 'Авізуючий банк',
  beneficiaryLoadingText: 'Завантаження даних бенефіціара...',
  beneficiarySelectAccountHint: 'Оберіть рахунок, щоб відредагувати інформацію про бенефіціара.',
  saveBeneficiaryButton: 'Зберегти інформацію про бенефіціара',
  baseDocumentNameLabel: 'Назва базового документа',
  baseDocumentNumberLabel: 'Номер базового документа',
  baseDocumentDateLabel: 'Дата базового документа',
  baseDocumentSubjectLabel: 'Предмет базового документа',
  baseDocumentAmountLabel: 'Сума базового документа',
  baseDocumentCurrencyLabel: 'Валюта базового документа',
  lockedUntilGuaranteeTypeSelected: 'Спочатку оберіть тип гарантії',
  saveGuaranteeTypeBeforeContinue: 'Збережіть тип гарантії, щоб продовжити.',
  contractRequiredHint: 'Оберіть та збережіть договір, щоб продовжити.',
  principalHasChangesInConstituentDocumentsLabel:
    'Наявність/відсутність змін в установчих документах Клієнта',
  pledgorHasChangesInConstituentDocumentsLabel:
    'Наявність/відсутність змін в установчих документах заставодавця',
  pledgorIdentifyCodeLabel: 'РНОКПП / ЄДРПОУ заставодавця',
} as const;

const guaranteesInProgressApplicationStatusConfig: Record<
  Exclude<
    GuaranteesApplicationStatus,
    | GuaranteesApplicationStatus.DRAFT
    | GuaranteesApplicationStatus.AWAITING_CLIENT_INITIAL_SIGN
    | GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER
    | GuaranteesApplicationStatus.AWAITING_PM_MANAGER_TERMS
    | GuaranteesApplicationStatus.AWAITING_UDBTF_ISSUANCE_TERMS
    | GuaranteesApplicationStatus.AWAITING_INTERNAL_APPROVALS
    | GuaranteesApplicationStatus.AWAITING_CLIENT_APPROVAL_LETTER_SIGN
    | GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN
    | GuaranteesApplicationStatus.AWAITING_UAKDO_APPROVAL
    | GuaranteesApplicationStatus.AWAITING_UAKDO_CONTROLLER_APPROVAL
    | GuaranteesApplicationStatus.AWAITING_UDBTF_APPROVAL
    | GuaranteesApplicationStatus.AWAITING_GUARANTEE_ISSUANCE
    | GuaranteesApplicationStatus.AWAITING_USAPOK_APPROVAL
    | GuaranteesApplicationStatus.AWAITING_USAPOK_CONTROLLER_APPROVAL
    | GuaranteesApplicationStatus.ISSUED
    | GuaranteesApplicationStatus.COMPLETED
    | GuaranteesApplicationStatus.REJECTED
  >,
  TGuaranteesApplicationStatusConfig
> = {
  [GuaranteesApplicationStatus.IN_PROGRESS_CLIENT_INITIAL_SIGN]: {
    label: 'Первинний підпис клієнта в роботі',
    color: 'info',
    description: 'Заявка перебуває на етапі первинного підпису клієнтом.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_AM_APPROVAL_LETTER]: {
    label: 'Лист-погодження AM в роботі',
    color: 'info',
    description: 'Лист-погодження AM опрацьовується менеджером.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_PM_MANAGER_TERMS]: {
    label: 'Умови PM-менеджера в роботі',
    color: 'info',
    description: 'PM-менеджер опрацьовує умови заявки.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_UDBTF_ISSUANCE_TERMS]: {
    label: 'Умови випуску від УДБТФ в роботі',
    color: 'info',
    description: 'Умови випуску гарантії опрацьовуються УДБТФ.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_INTERNAL_APPROVALS]: {
    label: 'Внутрішні погодження в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі внутрішніх погоджень банку.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_CLIENT_APPROVAL_LETTER_SIGN]: {
    label: 'Підпис листа-погодження клієнтом в роботі',
    color: 'info',
    description: 'Лист-погодження перебуває на етапі підпису клієнтом.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN]: {
    label: 'Ручний підпис листа-погодження AM в роботі',
    color: 'info',
    description:
      'AM менеджер опрацьовує завантаження підписаного вручну листа-погодження.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_UAKDO_APPROVAL]: {
    label: 'Погодження УАКДО в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі погодження УАКДО.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_UAKDO_CONTROLLER_APPROVAL]: {
    label: 'Контроль УАКДО в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі контрольного погодження УАКДО.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_UDBTF_APPROVAL]: {
    label: 'Погодження УДБТФ в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі погодження УДБТФ.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_GUARANTEE_ISSUANCE]: {
    label: 'Випуск гарантії в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі випуску гарантії.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_USAPOK_APPROVAL]: {
    label: 'Погодження УСАПОК в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі погодження УСАПОК.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_USAPOK_CONTROLLER_APPROVAL]: {
    label: 'Контроль УСАПОК в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі контрольного погодження УСАПОК.',
  },
};

export const guaranteesApplicationStatusConfig: Record<
  GuaranteesApplicationStatus,
  TGuaranteesApplicationStatusConfig
> = {
  ...guaranteesInProgressApplicationStatusConfig,
  [GuaranteesApplicationStatus.DRAFT]: {
    label: 'Чернетка',
    color: 'default',
    description: 'Заявка ще не відправлена до банку.',
  },
  [GuaranteesApplicationStatus.AWAITING_CLIENT_INITIAL_SIGN]: {
    label: 'Очікує підпису клієнта',
    color: 'warning',
    description: 'Заявка очікує первинного підпису клієнтом.',
  },
  [GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER]: {
    label: 'Очікує листа-погодження AM',
    color: 'warning',
    description: 'Очікується підготовка або погодження листа AM.',
  },
  [GuaranteesApplicationStatus.AWAITING_PM_MANAGER_TERMS]: {
    label: 'Очікує умов від PM-менеджера',
    color: 'warning',
    description: 'Очікується заповнення умов PM-менеджером.',
  },
  [GuaranteesApplicationStatus.AWAITING_UDBTF_ISSUANCE_TERMS]: {
    label: 'Очікує умов випуску від УДБТФ',
    color: 'warning',
    description: 'Очікується заповнення умов випуску гарантії УДБТФ.',
  },
  [GuaranteesApplicationStatus.AWAITING_INTERNAL_APPROVALS]: {
    label: 'Очікує внутрішніх погоджень',
    color: 'warning',
    description: 'Заявка очікує внутрішніх погоджень банку.',
  },
  [GuaranteesApplicationStatus.AWAITING_CLIENT_APPROVAL_LETTER_SIGN]: {
    label: 'Очікує підпису листа-погодження клієнтом',
    color: 'warning',
    description: 'Очікується підпис листа-погодження клієнтом.',
  },
  [GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN]: {
    label: 'Очікує ручного підпису листа-погодження AM',
    color: 'warning',
    description:
      'Очікується завантаження підписаного вручну листа-погодження AM.',
  },
  [GuaranteesApplicationStatus.AWAITING_UAKDO_APPROVAL]: {
    label: 'Очікує погодження УАКДО',
    color: 'warning',
    description: 'Заявка очікує погодження УАКДО.',
  },
  [GuaranteesApplicationStatus.AWAITING_UAKDO_CONTROLLER_APPROVAL]: {
    label: 'Очікує контролю УАКДО',
    color: 'warning',
    description: 'Заявка очікує контрольного погодження УАКДО.',
  },
  [GuaranteesApplicationStatus.AWAITING_UDBTF_APPROVAL]: {
    label: 'Очікує погодження УДБТФ',
    color: 'warning',
    description: 'Заявка очікує погодження УДБТФ.',
  },
  [GuaranteesApplicationStatus.AWAITING_GUARANTEE_ISSUANCE]: {
    label: 'Очікує випуску гарантії',
    color: 'warning',
    description: 'Заявка очікує випуску гарантії.',
  },
  [GuaranteesApplicationStatus.AWAITING_USAPOK_APPROVAL]: {
    label: 'Очікує погодження УСАПОК',
    color: 'warning',
    description: 'Заявка очікує погодження УСАПОК.',
  },
  [GuaranteesApplicationStatus.AWAITING_USAPOK_CONTROLLER_APPROVAL]: {
    label: 'Очікує контролю УСАПОК',
    color: 'warning',
    description: 'Заявка очікує контрольного погодження УСАПОК.',
  },
  [GuaranteesApplicationStatus.ISSUED]: {
    label: 'Гарантію випущено',
    color: 'success',
    description: 'Гарантію випущено.',
  },
  [GuaranteesApplicationStatus.COMPLETED]: {
    label: 'Завершено',
    color: 'success',
    description: 'Заявку завершено.',
  },
  [GuaranteesApplicationStatus.REJECTED]: {
    label: 'Відхилено',
    color: 'error',
    description: 'Заявку відхилено.',
  },
};
